import React from "react";
import { useNavigate } from "react-router-dom";

/**
 * Component to display when a call has ended
 * @param {Object} props - Component props
 * @param {number} props.callDuration - Duration of the call in seconds
 * @param {string} props.participantName - Name of the participant
 * @returns {JSX.Element} CallEndedScreen component
 */
const CallEndedScreen = ({ callDuration, participantName }) => {
  const navigate = useNavigate();

  // Handle navigation back to home/profile
  const handleBackToHome = () => {
    navigate("/profile");
  };

  return (
    <div className="fixed inset-0 bg-gradient-to-b from-white to-gray-100 flex items-center justify-center z-[9999]">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-1/4 w-64 h-64 rounded-full bg-[#A40165]/5 blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-80 h-80 rounded-full bg-[#A40165]/5 blur-3xl"></div>
        <div className="absolute top-1/3 right-1/3 w-40 h-40 rounded-full bg-purple-200/20 blur-2xl"></div>
      </div>

      <div className="w-full max-w-md mx-auto p-6 relative">
        <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl overflow-hidden border border-gray-200 p-8">
          {/* Call ended icon */}
          <div className="flex flex-col items-center">
            <div className="w-24 h-24 rounded-full bg-[#A40165]/10 flex items-center justify-center mb-6">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-12 w-12 text-[#A40165]"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"
                />
              </svg>
            </div>

            {/* Call ended title */}
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              Call Ended
            </h2>

            {/* Call details */}
            <div className="w-full bg-gray-50 rounded-xl p-4 mb-6">
              <div className="flex justify-between items-center mb-3">
                <span className="text-gray-600">Call with:</span>
                <span className="font-medium text-gray-800">
                  {participantName || "Participant"}
                </span>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex flex-col w-full gap-3">
              <button
                onClick={handleBackToHome}
                className="w-full py-3 px-4 bg-[#A40165] text-white rounded-xl font-medium hover:bg-[#8A0154] transition-colors duration-300 flex items-center justify-center"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 mr-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                  />
                </svg>
                Back to Home
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CallEndedScreen;
